/**
 * The meow, synthesised -- for now.
 *
 * Synthesised for the same reason the purr is: on the artifact face every
 * asset still inlines as base64 into a page that is ~4 MB already, so a
 * shipped recording is real page weight there -- and, more to the point,
 * the recording this feature is actually waiting for (Lily herself) does
 * not exist yet.  The sample path below is built and dormant; see "the
 * real recording" further down for the exact two-line swap when the file
 * arrives.
 *
 * Where the purr got lucky -- a purr is a rhythm, not a pitch -- the meow is
 * the opposite: it is nothing BUT pitch and vowel.  A meow is the one common
 * cat sound made with the mouth opening and then closing, which is why it
 * transcribes the same way in every language: a two-part vowel glide,
 * "mi-aou", voiced almost end to end.  Three things make it, and each one is
 * a block of numbers below:
 *
 *   - The SOURCE.  Vocal folds buzzing at a fundamental that house cats put
 *     roughly between 300 and 800 Hz, sliding along an arch: it starts a
 *     shade low (the closed-mouth "m"), rises to its peak about a third of
 *     the way in as the mouth opens, and falls away through the close.  A
 *     sawtooth is the standard stand-in for a glottal pulse train -- every
 *     harmonic present, rolling off ~6 dB/octave -- and it is those upper
 *     harmonics that give the formant filters something to carve.  On top
 *     of the arch sits a slight vibrato: a real larynx wavers a few Hz, and
 *     a perfectly steady meow reads instantly as a sound effect.
 *
 *   - The TRACT.  The mouth and throat resonate at a couple of movable
 *     frequencies (formants), and the vowel glide IS those resonances
 *     moving.  F1 tracks how open the mouth is: low for the shut-mouth
 *     "m"/"i" onset, peaking near 1.1 kHz at the wide "a", closing again
 *     into the "ou".  F2 tracks how bright the vowel is: high (~2.5 kHz)
 *     for the bright "i", falling steadily to ~1 kHz as the lips round into
 *     "ou".  Two bandpass filters riding those two curves, plus a lowpass
 *     "chest" path for body, are the whole tract.  No noise source: a meow
 *     is voiced nearly throughout, and the sawtooth's harmonics squeezed
 *     through narrow formants supply all the roughness it needs.
 *
 *   - The ENVELOPE.  A soft ~60 ms onset (the "m" swells, it does not
 *     click), a body that sags as the breath spends itself, and a close
 *     that lands with the pitch fall.  Attack-decay, nothing sustained: a
 *     meow is an utterance, not a tone.
 *
 * Every press rolls its own meow -- pitch, length, arch shape, vibrato,
 * tract size, and one meow in four ends on a rise (the interrogative meow
 * every cat owner knows).  Repeats out of one recipe with no scatter would
 * sound like a sample on loop, which is the one thing this must not be.
 *
 * Autoplay policy, exactly purr.js's: an AudioContext created before a user
 * gesture starts suspended, so the constructor touches no audio and
 * resume() -- reachable from begin()'s real click or keypress, and from
 * every meow trigger, all of which are real gestures -- builds everything.
 * Every public method is a no-op until then, and a context that cannot be
 * created (no WebAudio, a hostile iframe) leaves a silent but fully working
 * game.
 */

import { loadBytes } from "./assets/load.js";
import { getAdopting } from "./legacy-storage.js";

/**
 * ---- the real recording, when it arrives ---------------------------------
 *
 * The plan of record is a recording of the actual cat.  When the file lands
 * (packages/client/audio/meow.mp3 -- audio/README.md beside it has the
 * format and length budget), make exactly this change and nothing else:
 *
 *   set SAMPLE to "audio/meow".
 *
 * build.mjs's catalog already picks the recording up the moment the file
 * exists (any of .mp3/.ogg/.wav), so the id resolves on every target -- a
 * data: URI on the artifact face, where the page still makes zero network
 * requests, a hashed file on the web.  play() prefers the sample whenever
 * SAMPLE is set: build() loads and decodes it once, off the press, and each
 * press plays it back a few cents sharp or flat (SAMPLE_VARY) so repeats do
 * not read as a soundboard.  If the load or decode fails, the synthesised
 * meow below keeps covering -- the swap cannot break the feature.
 */
/** @type {string | null} */
const SAMPLE = null; // <- set to "audio/meow" when the recording arrives
/** Per-press detune of the sample, +/- semitones.  0.7 of one is inside the
    band a listener hears as "the same cat, a different meow" -- a full
    semitone starts to sound like a different cat. */
const SAMPLE_VARY = 0.7;

/** The fundamental's centre, Hz.  House-cat meows put F0 roughly 300-800 Hz;
    520 sits mid-range, and F0_SCATTER (a fraction of an octave, +/-) lets a
    press land anywhere from ~440 to ~615 so no two openers match. */
const F0_BASE = 520;
const F0_SCATTER = 0.24;
/** Utterance length, seconds.  Conversational meows run about half a second
    to just under one; the scatter inside this band is most of what makes a
    quick double-press sound like two meows rather than one echoed. */
const DUR_MIN = 0.45;
const DUR_MAX = 0.85;
/** The envelope's release past the nominal end -- the breath does not gate
    off, it empties.  Also the extra time the oscillators live. */
const TAIL = 0.12;
/** Vibrato: a real larynx wavers ~5-7 Hz at a couple of percent of F0.
    Depth is set per-press in play() (1.2-2.6% of F0). */
const VIB_HZ_MIN = 5.2;
const VIB_HZ_SPAN = 1.8;
/** One meow in four ends on a rise -- the question-mark meow.  Enough to
    keep a mashed button surprising, rare enough to stay a punctuation. */
const RISE_ODDS = 0.25;
/** Master ceiling.  The meow is an event where the purr is ambience, so it
    may peak briefly ABOVE the purr's bed -- but it is still one small cat,
    not a jump-scare: comfortably under speech volume. */
const MASTER = 0.55;
/** Presses closer together than this are one press.  A cat can chain meows
    ~5/s at the very most; anything faster is a bouncing key or a held key's
    auto-repeat leaking through, and layered copies of one voice phase into
    a chorus no animal makes. */
const MIN_GAP_MS = 170;

/** purr.js's MUTE_KEY, byte for byte.  One toggle governs the cat's whole
    voice: a player who muted the purr did not ask to be meowed at instead.
    Duplicated rather than imported so this module stays droppable on its
    own; the live Purr instance (via the `muted` hook) is still the first
    authority, and this key is only the fallback for a press that lands
    before the purr has been constructed. */
const MUTE_KEY = "catworld.purr.muted";

/** purr.js's LEGACY_MUTE_KEY, byte for byte, and adopted the same way. */
const LEGACY_MUTE_KEY = "lilysworld.purr.muted";

/** localStorage, or null where even touching it throws (sandboxed iframes,
    some private modes) -- the same posture as purr.js and store.js. */
function storage() {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
}

/**
 * Control points -> Float32Array for setValueCurveAtTime.
 *
 * Points are [position 0..1, value] pairs; between them the value eases on
 * a half-cosine rather than a straight line, because every one of these
 * curves stands in for a piece of anatomy (a pitch arch, a jaw, lips) and
 * flesh accelerates and settles -- a linear corner is audible as exactly
 * that, a corner.  64 samples is plenty: the curves below have four or five
 * bends over less than a second, and the AudioParam interpolates between
 * samples anyway.
 */
function curve(points, n = 64) {
  const c = new Float32Array(n);
  let seg = 0;
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    while (seg < points.length - 2 && t > points[seg + 1][0]) seg++;
    const [t0, v0] = points[seg];
    const [t1, v1] = points[seg + 1];
    const f = t1 > t0 ? Math.min(1, Math.max(0, (t - t0) / (t1 - t0))) : 1;
    c[i] = v0 + (v1 - v0) * (0.5 - 0.5 * Math.cos(f * Math.PI));
  }
  return c;
}

export class Meow {
  /**
   * @param opts.muted  optional hook returning the live mute state (main.js
   *                    passes () => purr?.muted).  Undefined -- purr not
   *                    built yet -- falls back to the stored MUTE_KEY.
   */
  constructor({ muted } = {}) {
    this.ctx = null;        // no AudioContext until a real user gesture
    this.master = null;
    this.sampleBuf = null;  // decoded recording, once SAMPLE is set and lands
    this.mutedHook = muted || null;
    this.lastAt = -Infinity;
  }

  /** Call from a real user gesture.  Safe to call again: it also re-resumes
      a context the browser has suspended (backgrounded mobile tab), which is
      why play() calls it too -- every meow trigger IS a gesture. */
  resume() {
    try {
      if (!this.ctx) {
        const AC = globalThis.AudioContext || globalThis.webkitAudioContext;
        if (!AC) return;
        this.ctx = new AC();
        this.build();
      }
      if (this.ctx.state === "suspended") this.ctx.resume().catch(() => {});
    } catch {
      // No audio here.  The meow is flavour, not the game; nothing throws on.
      this.ctx = null;
      this.master = null;
    }
  }

  build() {
    const ctx = this.ctx;
    // One master for every voice: the ceiling lives in one place, and a
    // per-press graph only ever plugs into it and dies.
    this.master = ctx.createGain();
    this.master.gain.value = MASTER;
    this.master.connect(ctx.destination);
    if (SAMPLE) {
      // Load and decode once, up front, off the press: both halves are
      // async and the first meow should not arrive a beat late.  A failed
      // load or decode is swallowed -- the synthesised path keeps covering.
      loadBytes(SAMPLE)
        .then((bytes) => ctx.decodeAudioData(bytes))
        .then((buf) => { this.sampleBuf = buf; })
        .catch(() => {});
    }
  }

  muted() {
    const live = this.mutedHook?.();
    if (live !== undefined && live !== null) return !!live;
    return getAdopting(storage(), MUTE_KEY, LEGACY_MUTE_KEY) === "1";
  }

  /**
   * One meow.  Returns the seconds the utterance will take -- the visual cue
   * holds the chin up for exactly this long -- or 0 when the press was
   * swallowed by the rate limit and the cue should not fire either.
   *
   * The variation is rolled even when the sound cannot play (muted, no
   * WebAudio): a muted player still gets the head-lift, and it still varies.
   */
  play() {
    const now = performance.now();
    if (now - this.lastAt < MIN_GAP_MS) return 0;
    this.lastAt = now;

    this.resume();          // every caller is a real gesture; see above

    // The roll: everything a press does differently from the last one.
    const dur = DUR_MIN + Math.random() * (DUR_MAX - DUR_MIN);
    const f0 = F0_BASE * Math.pow(2, (Math.random() * 2 - 1) * F0_SCATTER);
    const up = Math.random() < RISE_ODDS;

    if (this.ctx && this.master && !this.muted()) {
      if (this.sampleBuf) return this.playSample();
      this.playSynth(dur, f0, up);
    }
    return dur + TAIL;
  }

  /** The dormant drop-in path: the recording, a few cents off true. */
  playSample() {
    const ctx = this.ctx;
    const rate = Math.pow(2, ((Math.random() * 2 - 1) * SAMPLE_VARY) / 12);
    const src = ctx.createBufferSource();
    src.buffer = this.sampleBuf;
    src.playbackRate.value = rate;
    // 5 ms edges: a recording trimmed to the waveform's knee still clicks
    // when started raw, and a click on every press is the whole feature.
    const g = ctx.createGain();
    const t0 = ctx.currentTime + 0.01;
    const dur = this.sampleBuf.duration / rate;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(1, t0 + 0.005);
    g.gain.setValueAtTime(1, t0 + dur - 0.02);
    g.gain.linearRampToValueAtTime(0, t0 + dur);
    src.connect(g).connect(this.master);
    src.start(t0);
    return dur;
  }

  /** The recipe from the header, as a per-press node graph.  Everything is
      created fresh and stops itself: an event, unlike the purr's ever-running
      bed, and the start/stop churn purr.js avoids cannot click here because
      the envelope curve is already at zero at both ends of every voice. */
  playSynth(dur, f0, up) {
    const ctx = this.ctx;
    // 15 ms of scheduling headroom: curves stamped at exactly currentTime
    // can miss their first samples on a busy main thread.
    const t0 = ctx.currentTime + 0.015;
    const span = dur + TAIL;

    // The source: sawtooth riding the pitch arch.  The arch peaks about a
    // third in (where the jaw is widest), and the close either falls to
    // ~0.6x -- the ordinary declarative meow -- or lifts to ~1.15x for the
    // question-mark one.  The peak's position wanders a little per press so
    // even two meows at the same pitch arch differently.
    const peakAt = 0.28 + Math.random() * 0.14;
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueCurveAtTime(curve([
      [0, f0 * 0.80],
      [peakAt, f0],
      [0.62, f0 * 0.92],
      [1, f0 * (up ? 1.15 : 0.58)],
    ]), t0, span);

    // The waver: 5-7 Hz at 1.2-2.6% of F0, summed into the frequency param
    // on top of the arch (param automation and audio-rate input add).
    const vib = ctx.createOscillator();
    vib.type = "sine";
    vib.frequency.value = VIB_HZ_MIN + Math.random() * VIB_HZ_SPAN;
    const vibGain = ctx.createGain();
    vibGain.gain.value = f0 * (0.012 + Math.random() * 0.014);
    vib.connect(vibGain).connect(osc.frequency);

    // The tract: the whole per-press anatomy is one scale factor -- a
    // slightly bigger or smaller mouth -- applied to both formants, which is
    // how real variation works: the vowels move together or not at all.
    const mouth = 0.92 + Math.random() * 0.16;

    // F1, openness: shut for the "m" onset, wide (~1.1 kHz) at the "a",
    // closing into the "ou".  Q 7: narrow enough to be a vowel, wide enough
    // that a fundamental sliding under the band still gets through.
    const f1 = ctx.createBiquadFilter();
    f1.type = "bandpass";
    f1.Q.value = 7;
    f1.frequency.setValueCurveAtTime(curve([
      [0, 480 * mouth],
      [0.12, 560 * mouth],
      [peakAt + 0.1, 1120 * mouth],
      [0.6, 1000 * mouth],
      [1, 620 * mouth],
    ]), t0, span);

    // F2, brightness: the "i" glints near 2.5 kHz and the rounding lips pull
    // it down to ~1 kHz by the close.  Narrower (Q 9) and quieter than F1:
    // it is a colour, not the body of the sound.
    const f2 = ctx.createBiquadFilter();
    f2.type = "bandpass";
    f2.Q.value = 9;
    f2.frequency.setValueCurveAtTime(curve([
      [0, 2500 * mouth],
      [peakAt + 0.1, 1900 * mouth],
      [1, 1050 * mouth],
    ]), t0, span);

    // The chest: a gentle lowpass pass-through under the formants, so the
    // fundamental itself is always audible even while both formants sit
    // high.  Without it the voice is all vowel and no cat.
    const body = ctx.createBiquadFilter();
    body.type = "lowpass";
    body.frequency.value = 1100;
    body.Q.value = 0.5;

    // The mix: F1 carries the voice, F2 seasons it, the chest grounds it.
    const g1 = ctx.createGain();
    g1.gain.value = 1.0;
    const g2 = ctx.createGain();
    g2.gain.value = 0.4;
    const gb = ctx.createGain();
    gb.gain.value = 0.22;

    // The breath: swell in ~12% of the utterance (the soft "m"), sag through
    // the middle as the breath spends, close with the pitch fall, and a tail
    // that empties rather than gates.
    const env = ctx.createGain();
    env.gain.value = 0;
    env.gain.setValueCurveAtTime(curve([
      [0, 0],
      [0.12, 1],
      [0.5, 0.8],
      [0.78, 0.55],
      [dur / span, 0.3],
      [1, 0],
    ]), t0, span);

    osc.connect(f1).connect(g1).connect(env);
    osc.connect(f2).connect(g2).connect(env);
    osc.connect(body).connect(gb).connect(env);
    env.connect(this.master);

    osc.start(t0);
    vib.start(t0);
    // Stopped sources disconnect and collect themselves; the envelope is
    // already at zero when they die, so the stop cannot click.
    osc.stop(t0 + span);
    vib.stop(t0 + span);
  }
}
